import React from 'react';

/**
 * ConversationBriefCard
 *
 * Renders the Brain's ConversationBrief — the human-readable intelligence
 * summary that answers the 8 Stop Criteria questions:
 *   • Why did the AI ask this question?      → why_today
 *   • What mission is active?                → mission_id
 *   • What section is active?               → todays_goal
 *   • What tool was selected?               → top_opportunities
 *   • Was it verified?                      → (shown in subsystems tab)
 *   • What changed?                         → (shown in diff tab)
 *   • Why is summary locked?               → missing/weak sections
 *   • Which capability matched?            → (shown in contract grid above)
 *
 * Props:
 *   brief  — ConversationBrief dict from backend (can be null/empty)
 */
export default function ConversationBriefCard({ brief }) {
  if (!brief || !brief.todays_goal) {
    return (
      <div style={{
        padding: '16px',
        background: 'rgba(15,23,42,0.4)',
        border: '1px dashed rgba(100,116,139,0.3)',
        borderRadius: '10px',
        textAlign: 'center',
        color: '#64748b',
        fontSize: '11px',
      }}>
        No ConversationBrief yet — send a message to generate one.
      </div>
    );
  }

  const {
    profile_strength = 0,
    todays_goal = '',
    why_today = '',
    planner_decision = '',
    confidence = 0,
    mission_id = '',
    missing_sections = [],
    weak_sections = [],
    next_focus = '',
    remaining_focus_areas = [],
    top_opportunities = [],
  } = brief;

  const strengthPct = Math.round(profile_strength * 100);
  const confPct = Math.round(confidence * 100);

  const confColor = confPct >= 75 ? '#10b981' : confPct >= 50 ? '#f59e0b' : '#ef4444';
  const strengthColor = strengthPct >= 70 ? '#10b981' : strengthPct >= 40 ? '#f59e0b' : '#ef4444';

  const card = {
    padding: '12px 14px',
    background: 'rgba(15,23,42,0.5)',
    border: '1px solid rgba(100,116,139,0.2)',
    borderRadius: '10px',
    marginBottom: '10px',
  };

  const label = {
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#64748b',
    marginBottom: '3px',
    display: 'block',
  };

  const value = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#e2e8f0',
    lineHeight: 1.4,
  };

  return (
    <div style={{ fontSize: '11px', color: '#cbd5e1', lineHeight: 1.5 }}>

      {/* Profile Strength */}
      <div style={card}>
        <span style={label}>Profile Strength</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            flex: 1, height: '6px', background: 'rgba(100,116,139,0.2)',
            borderRadius: '99px', overflow: 'hidden',
          }}>
            <div style={{
              width: `${strengthPct}%`, height: '100%',
              background: strengthColor,
              borderRadius: '99px',
              transition: 'width 0.6s ease',
            }} />
          </div>
          <span style={{ fontSize: '12px', fontWeight: 700, color: strengthColor, minWidth: '32px' }}>
            {strengthPct}%
          </span>
        </div>
      </div>

      {/* Today's Goal + Why */}
      <div style={card}>
        <span style={label}>Today's Goal</span>
        <div style={{ ...value, color: '#a78bfa' }}>{todays_goal}</div>
        {why_today && (
          <div style={{ marginTop: '5px', fontStyle: 'italic', color: '#94a3b8', fontSize: '10px' }}>
            {why_today}
          </div>
        )}
      </div>

      {/* Planner Decision + Confidence */}
      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'center' }}>
        <div>
          <span style={label}>Planner Decision</span>
          <div style={{ ...value, fontFamily: 'monospace', fontSize: '10px', color: '#94a3b8' }}>
            {planner_decision || mission_id || '—'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={label}>Confidence</span>
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '99px',
            background: `${confColor}22`,
            border: `1px solid ${confColor}44`,
            color: confColor,
            fontWeight: 700,
            fontSize: '11px',
          }}>
            {confPct}%
          </span>
        </div>
      </div>

      {/* Missing Sections */}
      {missing_sections.length > 0 && (
        <div style={card}>
          <span style={label}>Missing Sections</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
            {missing_sections.map((s, i) => (
              <span key={i} style={{
                padding: '2px 8px',
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: '99px',
                color: '#fca5a5',
                fontSize: '10px',
                fontWeight: 600,
              }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Weak Sections */}
      {weak_sections.length > 0 && (
        <div style={card}>
          <span style={label}>Weak Sections</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
            {weak_sections.slice(0, 3).map((w, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <span style={{ color: '#f59e0b', fontSize: '10px' }}>⚠</span>
                <span style={{ color: '#fcd34d', fontSize: '10px' }}>
                  <strong>{w.section}</strong>{w.reason ? ` — ${w.reason}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Focus + Remaining */}
      {(next_focus || remaining_focus_areas.length > 0) && (
        <div style={card}>
          {next_focus && (
            <>
              <span style={label}>Next Focus</span>
              <div style={{ ...value, color: '#67e8f9', marginBottom: remaining_focus_areas.length ? '8px' : 0 }}>
                {next_focus}
              </div>
            </>
          )}
          {remaining_focus_areas.length > 0 && (
            <>
              <span style={{ ...label, marginTop: next_focus ? '6px' : 0 }}>Remaining Areas</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {remaining_focus_areas.slice(0, 6).map((r, i) => (
                  <span key={i} style={{
                    padding: '2px 8px',
                    background: 'rgba(100,116,139,0.15)',
                    border: '1px solid rgba(100,116,139,0.25)',
                    borderRadius: '99px',
                    color: '#94a3b8',
                    fontSize: '10px',
                  }}>{r}</span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Top Opportunities */}
      {top_opportunities.length > 0 && (
        <div style={card}>
          <span style={label}>Top Opportunities</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
            {top_opportunities.map((opp, i) => {
              const urgency = opp.urgency || '';
              const dotColor = urgency === 'HIGH' ? '#ef4444'
                : urgency === 'MEDIUM' ? '#f59e0b'
                : '#10b981';
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '6px',
                  padding: '6px 8px',
                  background: 'rgba(15,23,42,0.6)',
                  border: `1px solid ${dotColor}22`,
                  borderLeft: `2px solid ${dotColor}`,
                  borderRadius: '6px',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '11px' }}>
                      {opp.title || opp.dimension || '—'}
                    </div>
                    {opp.reason && (
                      <div style={{ color: '#64748b', fontSize: '10px', marginTop: '2px' }}>
                        {opp.reason}
                      </div>
                    )}
                  </div>
                  {opp.confidence !== undefined && (
                    <span style={{
                      fontSize: '10px', fontWeight: 700,
                      color: dotColor, whiteSpace: 'nowrap',
                    }}>
                      {Math.round(opp.confidence * 100)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
